import { useState } from "react";

import {
  exportToBlob,
  getTextFromElements,
  MIME_TYPES,
} from "@excalidraw/excalidraw";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";

import { getDataURL } from "@excalidraw/excalidraw/data/blob";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import {
  generatePresentationFromDiagram,
  hasGeminiApiKey,
} from "../data/gemini";

import "./DiagramToPresentation.scss";

import type { PresentationData } from "../data/gemini";

interface DiagramToPresentationProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
  onClose: () => void;
}

export const DiagramToPresentation = ({
  excalidrawAPI,
  onClose,
}: DiagramToPresentationProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [presentationData, setPresentationData] =
    useState<PresentationData | null>(null);
  const [error, setError] = useState<string>("");
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const handleGenerate = async () => {
    if (!hasGeminiApiKey()) {
      setError("Gemini API key not configured. Please set it in settings.");
      return;
    }

    setIsGenerating(true);
    setError("");

    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();

      if (elements.length === 0) {
        setError("No elements in the canvas. Please draw something first.");
        setIsGenerating(false);
        return;
      }

      const blob = await exportToBlob({
        elements,
        appState: {
          ...appState,
          exportBackground: true,
          viewBackgroundColor: appState.viewBackgroundColor,
        },
        files: excalidrawAPI.getFiles(),
        mimeType: MIME_TYPES.jpg,
      });

      const dataURL = await getDataURL(blob);
      const texts = getTextFromElements(elements);

      const presentation = await generatePresentationFromDiagram({
        imageBase64: dataURL,
        texts: texts ? [texts] : [],
        elementCount: elements.length,
        theme: appState.theme,
      });

      setPresentationData(presentation);
      setCurrentSlideIndex(0);
    } catch (err: any) {
      setError(err.message || "Failed to generate presentation");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = () => {
    if (!presentationData) {
      return;
    }

    const html = generatePresentationHTML(presentationData);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${presentationData.title.replace(/[^a-z0-9]/gi, "_")}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generatePresentationHTML = (data: PresentationData): string => {
    const isDark = data.theme === "dark";
    const bgColor = isDark ? "#1e1e1e" : "#ffffff";
    const textColor = isDark ? "#ffffff" : "#000000";
    const accentColor = isDark ? "#6965db" : "#5b57d9";

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${data.title}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            background: ${bgColor};
            color: ${textColor};
            overflow: hidden;
        }
        .presentation-container {
            width: 100vw;
            height: 100vh;
            display: flex;
            flex-direction: column;
        }
        .slide {
            display: none;
            width: 100%;
            height: 100%;
            padding: 60px 80px;
            animation: slideIn 0.5s ease;
        }
        .slide.active {
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
        @keyframes slideIn {
            from {
                opacity: 0;
                transform: translateX(50px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }
        .slide-title {
            font-size: 48px;
            font-weight: 700;
            color: ${accentColor};
            margin-bottom: 40px;
        }
        .slide-content {
            font-size: 24px;
            line-height: 1.6;
            white-space: pre-line;
            margin-bottom: 40px;
        }
        .speaker-notes {
            position: fixed;
            bottom: 20px;
            left: 20px;
            right: 20px;
            background: ${
              isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"
            };
            padding: 20px;
            border-radius: 8px;
            font-size: 14px;
            color: ${textColor};
            opacity: 0;
            transition: opacity 0.3s;
        }
        .speaker-notes.show {
            opacity: 1;
        }
        .speaker-notes strong {
            color: ${accentColor};
            font-size: 16px;
        }
        .controls {
            position: fixed;
            bottom: 20px;
            right: 20px;
            display: flex;
            gap: 10px;
        }
        button {
            padding: 12px 24px;
            font-size: 16px;
            border: none;
            background: ${accentColor};
            color: white;
            border-radius: 6px;
            cursor: pointer;
            transition: opacity 0.2s;
        }
        button:hover {
            opacity: 0.8;
        }
        button:disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
        .slide-counter {
            position: fixed;
            top: 20px;
            right: 20px;
            font-size: 18px;
            opacity: 0.5;
        }
    </style>
</head>
<body>
    <div class="presentation-container">
        <div class="slide-counter">
            <span id="current-slide">1</span> / <span id="total-slides">${
              data.slides.length
            }</span>
        </div>
        ${data.slides
          .map(
            (slide, index) => `
        <div class="slide${index === 0 ? " active" : ""}" data-slide="${index}">
            <h1 class="slide-title">${slide.title}</h1>
            <div class="slide-content">${slide.content}</div>
            <div class="speaker-notes" id="notes-${index}">
                <strong>Speaker Notes:</strong><br>
                ${slide.speakerNotes}
            </div>
        </div>
        `,
          )
          .join("")}
        <div class="controls">
            <button id="prev-btn">← Previous</button>
            <button id="notes-btn">Toggle Notes</button>
            <button id="next-btn">Next →</button>
        </div>
    </div>
    <script>
        let currentSlide = 0;
        const totalSlides = ${data.slides.length};
        const slides = document.querySelectorAll('.slide');
        const prevBtn = document.getElementById('prev-btn');
        const nextBtn = document.getElementById('next-btn');
        const notesBtn = document.getElementById('notes-btn');
        const currentSlideEl = document.getElementById('current-slide');
        let notesVisible = false;

        function showSlide(index) {
            slides.forEach((slide, i) => {
                slide.classList.toggle('active', i === index);
            });
            currentSlide = index;
            currentSlideEl.textContent = index + 1;
            prevBtn.disabled = index === 0;
            nextBtn.disabled = index === totalSlides - 1;
            if (notesVisible) {
                document.getElementById('notes-' + index).classList.add('show');
            }
        }

        prevBtn.addEventListener('click', () => {
            if (currentSlide > 0) showSlide(currentSlide - 1);
        });

        nextBtn.addEventListener('click', () => {
            if (currentSlide < totalSlides - 1) showSlide(currentSlide + 1);
        });

        notesBtn.addEventListener('click', () => {
            notesVisible = !notesVisible;
            document.querySelectorAll('.speaker-notes').forEach(notes => {
                notes.classList.toggle('show', notesVisible);
            });
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') prevBtn.click();
            if (e.key === 'ArrowRight') nextBtn.click();
            if (e.key === 'n' || e.key === 'N') notesBtn.click();
        });

        showSlide(0);
    </script>
</body>
</html>`;
  };

  return (
    <Dialog onCloseRequest={onClose} size="wide" title="Generate Presentation">
      <div className="diagram-to-presentation">
        {!presentationData ? (
          <div className="generation-section">
            <div className="description">
              <h3>Create a Presentation from Your Diagram</h3>
              <p>
                AI will analyze your diagram and create a structured
                presentation with:
              </p>
              <ul>
                <li>Progressive slide reveals</li>
                <li>Speaker notes for each slide</li>
                <li>Logical flow and structure</li>
                <li>Export as interactive HTML</li>
              </ul>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              className="generate-button"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? "Generating..." : "Generate Presentation"}
            </button>
          </div>
        ) : (
          <div className="preview-section">
            <div className="presentation-header">
              <h2>{presentationData.title}</h2>
              <div className="slide-navigation">
                <button
                  onClick={() =>
                    setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))
                  }
                  disabled={currentSlideIndex === 0}
                >
                  ← Previous
                </button>
                <span>
                  Slide {currentSlideIndex + 1} of{" "}
                  {presentationData.slides.length}
                </span>
                <button
                  onClick={() =>
                    setCurrentSlideIndex(
                      Math.min(
                        presentationData.slides.length - 1,
                        currentSlideIndex + 1,
                      ),
                    )
                  }
                  disabled={
                    currentSlideIndex === presentationData.slides.length - 1
                  }
                >
                  Next →
                </button>
              </div>
            </div>

            <div className="slide-preview">
              {presentationData.slides[currentSlideIndex] && (
                <>
                  <h3>{presentationData.slides[currentSlideIndex].title}</h3>
                  <div className="slide-content">
                    {presentationData.slides[currentSlideIndex].content}
                  </div>
                  <div className="speaker-notes">
                    <strong>Speaker Notes:</strong>
                    <p>
                      {presentationData.slides[currentSlideIndex].speakerNotes}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="action-buttons">
              <button onClick={() => setPresentationData(null)}>
                Generate New
              </button>
              <button className="export-button" onClick={handleExport}>
                Export as HTML
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
